import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Plus, Save, ExternalLink, FileText, ArrowLeft, Receipt, CalendarClock, Layers, Video, Share2, Mic, Trash2, Download, MessageCircle, ArrowRightLeft, Search, Loader2, MessageSquare } from "lucide-react";
import { initials, ageDisplay, fmtDateTime, todayISO, displayTime, timeSlots } from "@/lib/format";
import BillDialog from "@/components/dialogs/BillDialog";
import BulletField from "@/components/BulletField";
import MedicineSearch from "@/components/dialogs/MedicineSearch";
import MedicationRow, { medFromMedicine, emptyMed } from "@/components/MedicationRow";
import VideoCallDialog, { meetingLinkFor } from "@/components/VideoCallDialog";
import VoiceConsultDialog from "@/components/VoiceConsultDialog";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Build a WhatsApp click-to-chat URL: opens WhatsApp with the message pre-filled
 * to the patient's mobile. Number is normalized to digits (WhatsApp wa.me expects
 * international format without '+'). */
function waLink(mobile, shareUrl, name) {
  const digits = String(mobile || "").replace(/[^\d]/g, "");
  const first = (name || "").trim().split(" ")[0];
  const greeting = first ? `Hi ${first}, ` : "Hi, ";
  const msg = `${greeting}here is your prescription from your recent consultation:\n${shareUrl}\n\nPlease follow the dosage as advised. Take care 🌿`;
  return `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
}

/** Fetch the prescription PDF via the public share URL and open it as a blob URL
 * in a new tab. This sidesteps any browser/ad-blocker content-sniffing that can
 * block a direct `<a href="…/prescription/*.pdf">` navigation. */
async function openRxPdf(shareToken) {
  try {
    const res = await fetch(`${API_URL}/share/prescription/${shareToken}.pdf`, { credentials: "omit" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    // Open in a new tab; delayed revoke gives Chrome time to render the PDF.
    const w = window.open(url, "_blank", "noopener,noreferrer");
    if (!w) {
      // Popup blocked — fall back to same-window navigation.
      window.location.href = url;
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    alert(`Could not open prescription PDF: ${e?.message || e}`);
  }
}
import { useAuth } from "@/lib/auth";

const emptyLab = () => ({ name: "", urgency: "routine", notes: "" });

export default function Consultation() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  // RBAC: nurse-role staff can open the consultation to view patient history +
  // capture vitals, but cannot see/write Diagnosis, Prescription or Complete.
  const isNurseOnly = user?.role === "staff" && (user?.staff_roles || []).includes("nurse")
                   && !(user?.staff_roles || []).includes("doctor");
  // RBAC: pharmacist and lab_tech staff have no reason to open the consultation
  // form — their actions live on /workflow. Redirect them there defensively.
  const isPharmacistOrLab = user?.role === "staff" && (
    (user?.staff_roles || []).includes("pharmacist") ||
    (user?.staff_roles || []).includes("lab_tech")
  ) && !(user?.staff_roles || []).includes("nurse")
    && !(user?.staff_roles || []).includes("receptionist");
  const patientId = sp.get("patient");
  const appointmentId = sp.get("appt");
  const [patient, setPatient] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const [openCall, setOpenCall] = useState(false);
  const [history, setHistoryConsults] = useState([]);
  const [reports, setReports] = useState([]);

  const [cc, setCC] = useState("");
  const [histList, setHistList] = useState([]);
  const [examList, setExamList] = useState([]);
  const [adviceList, setAdviceList] = useState([]);
  const [bp, setBp] = useState(""); const [pulse, setPulse] = useState("");
  const [temp, setTemp] = useState(""); const [spo2, setSpo2] = useState("");
  const [weight, setWeight] = useState(""); const [height, setHeight] = useState("");
  const [dx, setDx] = useState("");

  const [fuDate, setFuDate] = useState("");
  const [fuTime, setFuTime] = useState("");
  const [fuNote, setFuNote] = useState("");

  // Referral (Phase 2): allow referring this consultation to another doctor in the Care Circle.
  const [isReferral, setIsReferral] = useState(false);
  const [refShareHistory, setRefShareHistory] = useState(false);
  const [refSpeciality, setRefSpeciality] = useState("");
  const [refNote, setRefNote] = useState("");
  const [refToDoctorId, setRefToDoctorId] = useState("");
  const [ccMembers, setCcMembers] = useState([]);
  const [ccLoading, setCcLoading] = useState(false);

  const [meds, setMeds] = useState([]);
  const [labs, setLabs] = useState([emptyLab()]);
  const [rxGroups, setRxGroups] = useState([]);
  const [showRxPicker, setShowRxPicker] = useState(false);

  const [aiBusy, setAiBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const [openBill, setOpenBill] = useState(false);
  const [openVoice, setOpenVoice] = useState(false);
  const [startedAt] = useState(() => Date.now());
  const slots = useMemo(() => timeSlots(7, 22, 15), []);

  useEffect(() => {
    if (isPharmacistOrLab) {
      toast.info("This screen is for doctors. Redirecting to Workflow…");
      nav("/journey", { replace: true });
    }
  }, [isPharmacistOrLab, nav]);

  useEffect(() => {
    if (isPharmacistOrLab) return;   // will be redirected — skip data load
    if (!patientId) { toast.error("No patient specified"); nav("/dashboard"); return; }
    (async () => {
      // Use allSettled so a single 404 (e.g. rx-groups denied for staff) doesn't
      // sink the whole render. Patient GET is the only hard requirement.
      const [pr, cr, rr, gr] = await Promise.allSettled([
        api.get(`/patients/${patientId}`),
        api.get(`/consultations?patient_id=${patientId}`),
        api.get(`/reports?patient_id=${patientId}`),
        api.get(`/rx-groups`),
      ]);
      if (pr.status === "fulfilled") setPatient(pr.value.data);
      else { toast.error("Failed to load patient"); return; }
      if (cr.status === "fulfilled") setHistoryConsults(cr.value.data);
      if (rr.status === "fulfilled") setReports(rr.value.data);
      if (gr.status === "fulfilled") setRxGroups((gr.value.data || []).filter((g) => g.enabled !== false));
      if (appointmentId) {
        try {
          const { data: a } = await api.get(`/appointments/${appointmentId}`);
          setAppointment(a);
        } catch { /* ignore */ }
      }
    })();
  }, [patientId, appointmentId, nav, isPharmacistOrLab]);

  useEffect(() => {
    if (!isReferral) return;
    if (ccMembers.length > 0) return;
    setCcLoading(true);
    api.get("/care-circle/members")
      .then(({ data }) => setCcMembers(data || []))
      .catch(() => toast.error("Could not load care-circle members"))
      .finally(() => setCcLoading(false));
  }, [isReferral, ccMembers.length]);

  const vitals = useMemo(() => ({ bp, pulse, temp, spo2, weight, height }), [bp, pulse, temp, spo2, weight, height]);

  const filteredCcMembers = useMemo(() => {
    const q = (refSpeciality || "").trim().toLowerCase();
    if (!q) return ccMembers;
    return ccMembers.filter((m) =>
      (m.specialties || []).some((s) => String(s).toLowerCase().includes(q)) ||
      String(m.name || "").toLowerCase().includes(q)
    );
  }, [ccMembers, refSpeciality]);

  const doDraft = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/draft", {
        patient_id: patientId,
        chief_complaint: cc,
        history: histList.join(". "),
        vitals, examination: examList.join(". "),
        notes: `Advice so far: ${adviceList.join(". ")}`,
      });
      if (data.diagnosis) setDx(data.diagnosis);
      if (data.advice) {
        const items = String(data.advice).split(/[.;\n]+/).map((s) => s.trim()).filter(Boolean);
        setAdviceList(items.length ? items : [data.advice]);
      }
      if (data.follow_up) setFuNote(data.follow_up);
      if (Array.isArray(data.medications) && data.medications.length) {
        setMeds(data.medications.map((m) => ({
          ...emptyMed(),
          name: m.name || "",
          strength: m.dosage || m.strength || "",
          instructions: m.instructions || "",
        })));
      }
      if (Array.isArray(data.lab_orders) && data.lab_orders.length)
        setLabs(data.lab_orders.map((l) => ({ ...emptyLab(), ...l })));
      toast.success("AI draft ready — review before sharing");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI draft failed");
    } finally { setAiBusy(false); }
  };

  const applyRxGroup = (g) => {
    setMeds([...(meds || []), ...(g.drugs || []).map((d) => ({ ...d }))]);
    toast.success(`Applied "${g.name}" (${(g.drugs || []).length} drugs)`);
    setShowRxPicker(false);
  };

  const addMedicine = (m) => setMeds([...(meds || []), medFromMedicine(m)]);

  /** Merge AI-structured voice output into the current consultation form.
   * We APPEND to list-based fields (history/examination/advice/meds/labs) and
   * OVERWRITE scalars (chief complaint / diagnosis / vitals / follow-up) only
   * when the AI actually returned a value — so a partial dictation never
   * wipes out what the doctor typed manually. */
  const applyVoice = (s) => {
    if (!s) return;
    let touched = 0;
    if (s.chief_complaint) { setCC(s.chief_complaint); touched++; }
    if (s.diagnosis) { setDx(s.diagnosis); touched++; }
    if (Array.isArray(s.history) && s.history.length) { setHistList((prev) => [...prev, ...s.history]); touched++; }
    if (Array.isArray(s.examination) && s.examination.length) { setExamList((prev) => [...prev, ...s.examination]); touched++; }
    if (Array.isArray(s.advice) && s.advice.length) { setAdviceList((prev) => [...prev, ...s.advice]); touched++; }
    if (s.vitals && typeof s.vitals === "object") {
      if (s.vitals.bp) { setBp(String(s.vitals.bp)); touched++; }
      if (s.vitals.pulse) { setPulse(String(s.vitals.pulse)); touched++; }
      if (s.vitals.temp) { setTemp(String(s.vitals.temp)); touched++; }
      if (s.vitals.spo2) { setSpo2(String(s.vitals.spo2)); touched++; }
      if (s.vitals.weight) { setWeight(String(s.vitals.weight)); touched++; }
      if (s.vitals.height) { setHeight(String(s.vitals.height)); touched++; }
    }
    if (s.follow_up_date) { setFuDate(s.follow_up_date); touched++; }
    if (s.follow_up_time) { setFuTime(s.follow_up_time); touched++; }
    if (s.follow_up_note) { setFuNote(s.follow_up_note); touched++; }
    if (Array.isArray(s.medications) && s.medications.length) {
      const mapped = s.medications.map((m) => ({
        ...emptyMed(),
        name: m.name || "",
        form: m.form || "",
        strength: m.strength || "",
        dose: (m.dose && typeof m.dose === "object")
          ? { morning: Number(m.dose.morning || 0), afternoon: Number(m.dose.afternoon || 0), night: Number(m.dose.night || 0) }
          : undefined,
        when_food: m.when_food || "",
        duration: m.duration ? Number(m.duration) : undefined,
        duration_unit: m.duration_unit || "",
        qty: m.qty ? Number(m.qty) : undefined,
        instructions: m.instructions || "",
      }));
      setMeds((prev) => [...(prev || []), ...mapped]);
      touched++;
    }
    if (Array.isArray(s.lab_orders) && s.lab_orders.length) {
      const cleaned = (labs || []).filter((l) => (l.name || "").trim());
      setLabs([...cleaned, ...s.lab_orders.map((l) => ({ ...emptyLab(), ...l }))]);
      touched++;
    }
    if (touched > 0) toast.success(`Voice applied — ${touched} field${touched === 1 ? "" : "s"} updated`);
    else toast.info("Voice output was empty — try again");
  };

  const save = async () => {
    setSaveBusy(true);
    try {
      const payload = {
        appointment_id: appointmentId || null,
        patient_id: patientId,
        chief_complaint: cc,
        history_list: histList, examination_list: examList, advice_list: adviceList,
        history: histList.join(". "), examination: examList.join(". "), advice: adviceList.join(". "),
        vitals, diagnosis: dx,
        follow_up_date: fuDate || null,
        follow_up_time: fuTime || null,
        follow_up_note: fuNote || null,
        follow_up: fuDate && fuTime ? `${fuDate} ${fuTime} — ${fuNote || "Follow-up"}` : (fuNote || null),
        medications: (meds || []).filter((m) => (m.name || "").trim()),
        lab_orders: (labs || []).filter((l) => (l.name || "").trim()),
        // Referral (Phase 2) — only send when explicitly enabled AND a target picked.
        is_referral: !!(isReferral && refToDoctorId),
        referral_to_doctor_id: (isReferral && refToDoctorId) ? refToDoctorId : null,
        referral_share_history: !!(isReferral && refToDoctorId && refShareHistory),
        referral_speciality: (isReferral && refToDoctorId) ? (refSpeciality || null) : null,
        referral_note: (isReferral && refToDoctorId) ? (refNote || null) : null,
        duration_seconds: Math.max(1, Math.round((Date.now() - startedAt) / 1000)),
      };
      const { data } = await api.post("/consultations", payload);
      setSaved(data);
      const emailNote = data?.email_sent
        ? ` Prescription emailed to ${data.emailed_to}.`
        : (patient?.email ? " Prescription PDF saved. (Email attempt failed — copy the link instead.)" : " Prescription PDF saved. Add a patient email to enable auto-send.");
      if (fuDate && fuTime) toast.success("Saved. Follow-up appointment auto-created." + emailNote);
      else toast.success("Consultation saved." + emailNote);
      if (isReferral && refToDoctorId) {
        const to = ccMembers.find((m) => m.id === refToDoctorId);
        toast.success(`Referral sent${to ? ` to ${to.name}` : ""}${refShareHistory ? " · history shared" : ""}.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaveBusy(false); }
  };

  const shareUrl = saved ? `${window.location.origin}/share/prescription/${saved.share_token}` : "";
  const copyLink = async () => {
    if (!shareUrl) return;
    await navigator.clipboard.writeText(shareUrl);
    toast.success("Prescription link copied");
  };

  if (!patient) return <Layout><div className="py-20 text-center text-[#8A9992]">Loading…</div></Layout>;

  const shareCallLink = async () => {
    const link = meetingLinkFor(appointment || { id: appointmentId, meeting_room: null });
    if (!link) { toast.error("No appointment context"); return; }
    await navigator.clipboard.writeText(link);
    toast.success("Meeting link copied");
  };

  return (
    <Layout>
      {/* Header: back + patient identity only. Primary actions now live inside the
          Live consultation card (top-right) — matches the request to reduce header clutter. */}
      <div className="mb-5 md:mb-6">
        <div className="flex items-center justify-between gap-3">
          <button className="inline-flex items-center gap-2 text-[#47554D] text-sm hover:text-[#0A4C36]" onClick={() => nav(-1)} data-testid="consult-back-btn">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-full bg-[#0A4C36]/10 text-[#0A4C36] flex items-center justify-center font-semibold text-xs flex-none">
              {initials(patient.name)}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[#1F2A24] truncate">{patient.name}</div>
              <div className="text-[11px] text-[#8A9992] truncate">{patient.gender} · {ageDisplay(patient)}</div>
            </div>
          </div>
        </div>
      </div>

      {saved && (
        <div className="card-soft p-4 mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid="saved-banner">
          <div className="text-sm text-[#1F2A24]">
            <div className="font-semibold">
              Prescription saved{saved.emailed_to ? ` — emailed to ${saved.emailed_to}.` : "."}
            </div>
            <div className="text-xs text-[#0A4C36] font-mono break-all mt-1">{shareUrl}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => openRxPdf(saved.share_token)}
              className="btn-outline-emerald"
              data-testid="download-pdf-btn"
            >
              <FileText size={14} /> Open PDF
            </button>
            <a href={shareUrl} target="_blank" rel="noreferrer" className="btn-outline-emerald" data-testid="open-share-btn"><ExternalLink size={14} /> Preview</a>
            <button onClick={copyLink} className="btn-emerald" data-testid="copy-share-btn"><Share2 size={14} /> Copy link</button>
            {patient?.mobile && (
              <a
                href={waLink(patient.mobile, shareUrl, patient.name)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-4 py-2 text-sm font-semibold hover:bg-[#1EBE5A] transition-colors"
                data-testid="whatsapp-share-btn"
                title={`Send to ${patient.mobile} on WhatsApp`}
              >
                <MessageCircle size={14} /> WhatsApp
              </a>
            )}
            <button onClick={() => setOpenBill(true)} className="btn-peach" data-testid="create-bill-btn"><Receipt size={14} /> Create bill</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="consult-grid">
        {/* Left: History */}
        <aside className="lg:col-span-4 xl:col-span-3 card-soft p-5 md:p-6 lg:sticky lg:top-6 self-start lg:max-h-[calc(100vh-100px)] overflow-y-auto" data-testid="history-column">
          <div className="field-label mb-4">Patient history</div>
          <div className="space-y-3">
            {history.length === 0 && (
              <div className="text-xs text-[#8A9992] py-6 text-center bg-[#F7F4EC]/60 rounded-xl border border-dashed border-[#E8E6E1]">
                No past consultations yet.
              </div>
            )}
            {history.map((c) => (
              <div
                key={c.id}
                className="rounded-2xl bg-[#F7F4EC]/70 border border-[#EFECE5] p-3.5 hover:bg-[#F7F4EC] transition-colors"
                data-testid={`history-card-${c.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#8A9992]">
                    <CalendarClock size={11} /> {fmtDateTime(c.created_at)}
                  </div>
                  {c.share_token && (
                    <button
                      type="button"
                      onClick={() => openRxPdf(c.share_token)}
                      className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#0A4C36] hover:text-[#073B29] hover:underline"
                      title="Open prescription PDF"
                      data-testid={`history-pdf-${c.id}`}
                    >
                      <FileText size={11} /> PDF
                    </button>
                  )}
                </div>
                <div className="font-semibold text-sm text-[#1F2A24] mt-2 leading-snug">
                  {c.diagnosis || "Consultation"}
                </div>
                {(c.second_opinions || []).length > 0 && (
                  <div className="mt-1.5 text-[10px] font-semibold text-[#0A4C36] inline-flex items-center gap-1" data-testid={`history-so-${c.id}`}>
                    <MessageSquare size={10} /> +{c.second_opinions.length} second opinion{c.second_opinions.length > 1 ? "s" : ""}
                  </div>
                )}
                {(c.medications || []).length > 0 && (
                  <div className="text-[12px] text-[#5C7568] mt-1.5 leading-snug">
                    {c.medications.slice(0, 3).map((m) => m.name).filter(Boolean).join(" · ")}
                    {c.medications.length > 3 && <span className="text-[#8A9992]"> +{c.medications.length - 3} more</span>}
                  </div>
                )}
                {c.emailed_to && (
                  <div className="text-[10px] text-[#0A4C36]/70 mt-2 inline-flex items-center gap-1">
                    <Download size={10} /> Emailed to {c.emailed_to.length > 24 ? c.emailed_to.slice(0, 22) + "…" : c.emailed_to}
                  </div>
                )}
              </div>
            ))}

            {reports.length > 0 && (
              <>
                <div className="divider-soft my-4" />
                <div className="field-label mb-2">Reports</div>
                <div className="space-y-2">
                  {reports.slice(0, 6).map((r) => (
                    <div key={r.id} className="rounded-xl bg-white border border-[#EFECE5] p-2.5 text-xs" data-testid={`report-card-${r.id}`}>
                      <div className="font-medium text-[#1F2A24]">{r.title}</div>
                      <div className="text-[#8A9992] text-[11px] mt-0.5">{r.category} · {fmtDateTime(r.created_at)}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </aside>

        {/* Right: Main entry + orders as full-width sub-sections */}
        <section className="lg:col-span-8 xl:col-span-9 space-y-5 md:space-y-6" data-testid="entry-column">
          <div className="card-soft p-5 md:p-8">
            <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
              <div className="field-label">Live consultation</div>
              {/* Primary actions live here (top-right of the entry card) so the doctor's
                  fingers land on them exactly where their eyes already are.
                  Hidden for nurse — she cannot draft/save/prescribe. */}
              {!isNurseOnly && (
              <div className="flex flex-wrap items-center gap-2" data-testid="entry-actions">
                <button onClick={() => setOpenVoice(true)} className="btn-emerald relative" data-testid="voice-consult-btn" title="Dictate the whole consultation">
                  <Mic size={14} /> <span className="hidden sm:inline">Voice</span>
                  <span className="absolute -top-1 -right-1 text-[9px] font-semibold bg-[#FCE7D9] text-[#5C3A21] rounded-full px-1.5 py-0.5 border border-white">AI</span>
                </button>
                <button onClick={doDraft} disabled={aiBusy} className="btn-peach" data-testid="ai-draft-btn">
                  <Sparkles size={14} /> <span className="hidden sm:inline">{aiBusy ? "Drafting…" : "AI draft"}</span><span className="sm:hidden">Draft</span>
                </button>
                {appointmentId && (
                  <>
                    <button onClick={() => setOpenCall(true)} className="btn-outline-emerald" data-testid="consult-video-btn">
                      <Video size={14} /> <span className="hidden sm:inline">Video</span>
                    </button>
                    <button onClick={shareCallLink} className="btn-outline-emerald" data-testid="consult-share-link-btn">
                      <Share2 size={14} /> <span className="hidden sm:inline">Share</span>
                    </button>
                  </>
                )}
                <button onClick={save} disabled={saveBusy} className="btn-emerald" data-testid="save-consult-btn">
                  <Save size={14} /> {saveBusy ? "Saving…" : <><span className="hidden sm:inline">Save & prescribe</span><span className="sm:hidden">Save</span></>}
                </button>
              </div>
              )}
            </div>
            <div className="space-y-5">
              <Row label="Chief complaint">
                <input value={cc} onChange={(e) => setCC(e.target.value)} className="input-clean" placeholder="e.g. Dry cough for 3 days" data-testid="cc-input" />
              </Row>
              <Row label="History of present illness" hint="Type each point and press Enter to bullet it. Click a bullet to edit.">
                <BulletField values={histList} onChange={setHistList} placeholder="e.g. Onset 3 days ago…" testId="hist" />
              </Row>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                <Vital label="BP" v={bp} setV={setBp} placeholder="120/80" testId="vitals-bp" />
                <Vital label="Pulse" v={pulse} setV={setPulse} placeholder="72" testId="vitals-pulse" />
                <Vital label="Temp" v={temp} setV={setTemp} placeholder="98.4°F" testId="vitals-temp" />
                <Vital label="SpO₂" v={spo2} setV={setSpo2} placeholder="98%" testId="vitals-spo2" />
                <Vital label="Weight" v={weight} setV={setWeight} placeholder="kg" testId="vitals-weight" />
                <Vital label="Height" v={height} setV={setHeight} placeholder="cm" testId="vitals-height" />
              </div>
              <Row label="Examination / key findings" hint="Enter each finding as a bullet.">
                <BulletField values={examList} onChange={setExamList} placeholder="e.g. Throat congested, no exudate" testId="exam" />
              </Row>
              {!isNurseOnly && (
              <Row label="Diagnosis" hint="Free text — add multiple lines for provisional, differential, and rationale.">
                <textarea
                  value={dx}
                  onChange={(e) => setDx(e.target.value)}
                  className="input-clean min-h-[92px] resize-y leading-relaxed"
                  rows={3}
                  placeholder="e.g. Acute viral pharyngitis (provisional). Rule out streptococcal pharyngitis if symptoms persist > 5 days."
                  data-testid="dx-input"
                />
              </Row>
              )}
              {!isNurseOnly && (
              <Row label="Advice" hint="Enter each instruction as a bullet.">
                <BulletField values={adviceList} onChange={setAdviceList} placeholder="e.g. Warm saline gargle 3× a day" testId="advice" />
              </Row>
              )}
              {!isNurseOnly && (
              <Row label="Follow-up (auto-creates appointment)" hint="Pick a date + time and add a short note. We'll book it for you.">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <input type="date" value={fuDate} onChange={(e) => setFuDate(e.target.value)} className="input-clean" min={todayISO()} data-testid="fu-date" />
                  <select value={fuTime} onChange={(e) => setFuTime(e.target.value)} className="input-clean" data-testid="fu-time">
                    <option value="">Select time</option>
                    {slots.map((s) => <option key={s} value={s}>{displayTime(s)}</option>)}
                  </select>
                  <input value={fuNote} onChange={(e) => setFuNote(e.target.value)} placeholder="Note (e.g. review CBC)" className="input-clean" data-testid="fu-note" />
                </div>
                {fuDate && fuTime && (
                  <div className="mt-2 text-xs text-[#0A4C36] inline-flex items-center gap-2">
                    <CalendarClock size={14} /> Will book on {fuDate} at {displayTime(fuTime)}
                  </div>
                )}
              </Row>
              )}

              {/* Referral (Phase 2) — refer this patient to a colleague in the Care Circle */}
              {!isNurseOnly && (
              <div className="rounded-2xl border border-[#EFECE5] bg-[#F7F4EC]/40 p-4" data-testid="referral-block">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isReferral}
                    onChange={(e) => setIsReferral(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-[#0A4C36]"
                    data-testid="referral-toggle"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-[#1F2A24] inline-flex items-center gap-2">
                      <ArrowRightLeft size={14} className="text-[#0A4C36]" /> Refer this patient to a colleague
                    </div>
                    <div className="text-[11px] text-[#8A9992] mt-0.5">
                      Sends a referral to a doctor in your Care Circle. They&rsquo;ll be notified in-app.
                    </div>
                  </div>
                </label>

                {isReferral && (
                  <div className="mt-4 space-y-3" data-testid="referral-panel">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <div className="text-[11px] uppercase tracking-widest text-[#5C7568] mb-1">Filter by speciality or name</div>
                        <div className="relative">
                          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A9992]" />
                          <input
                            value={refSpeciality}
                            onChange={(e) => setRefSpeciality(e.target.value)}
                            className="input-clean pl-8"
                            placeholder="e.g. Cardiology"
                            data-testid="referral-speciality"
                          />
                        </div>
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-widest text-[#5C7568] mb-1">Refer to</div>
                        {ccLoading ? (
                          <div className="input-clean inline-flex items-center gap-2 text-[#8A9992]">
                            <Loader2 size={12} className="animate-spin" /> Loading care circle…
                          </div>
                        ) : (
                          <select
                            value={refToDoctorId}
                            onChange={(e) => setRefToDoctorId(e.target.value)}
                            className="input-clean"
                            data-testid="referral-to-doctor"
                          >
                            <option value="">
                              {filteredCcMembers.length === 0
                                ? (ccMembers.length === 0 ? "No members — add colleagues in Care Circle" : "No match — clear filter")
                                : "Select a colleague"}
                            </option>
                            {filteredCcMembers.map((m) => (
                              <option key={m.id} value={m.id}>
                                {m.name}{(m.specialties || []).length ? ` — ${m.specialties.join(", ")}` : ""}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>

                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-[#5C7568] mb-1">Note to the referred doctor</div>
                      <textarea
                        value={refNote}
                        onChange={(e) => setRefNote(e.target.value)}
                        rows={2}
                        className="input-clean resize-y"
                        placeholder="e.g. Please evaluate for cardiac cause of chest pain."
                        data-testid="referral-note"
                      />
                    </div>

                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={refShareHistory}
                        onChange={(e) => setRefShareHistory(e.target.checked)}
                        className="mt-0.5 w-4 h-4 accent-[#0A4C36]"
                        data-testid="referral-consent"
                      />
                      <div className="text-xs text-[#47554D]">
                        <span className="font-semibold text-[#1F2A24]">Patient consents to share history.</span>{" "}
                        The referred doctor will get read-access to this patient&rsquo;s consultation history &amp; reports.
                      </div>
                    </label>

                    {ccMembers.length === 0 && !ccLoading && (
                      <div className="text-[11px] text-[#B14A2A]">
                        You have no members in your Care Circle yet. Add colleagues from the <b>Care Circle</b> tab to enable referrals.
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}
            </div>
          </div>

          {/* Prescription — now full-width, meds have room to breathe */}
          {!isNurseOnly && (
          <>
          <div className="card-soft p-5 md:p-7 bg-[#F8F5EF]" data-testid="orders-column">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <div className="field-label">Prescription</div>
              <button
                className="text-xs text-[#0A4C36] font-semibold inline-flex items-center gap-1 hover:underline"
                onClick={() => setShowRxPicker((s) => !s)}
                data-testid="apply-rx-toggle"
              >
                <Layers size={12} /> Apply Rx group
              </button>
            </div>

            {showRxPicker && (
              <div className="card-soft p-3 mb-4 border border-[#EFECE5]" data-testid="rx-picker">
                <div className="text-xs text-[#8A9992] mb-2">Your Rx groups</div>
                {rxGroups.length === 0 && <div className="text-xs text-[#8A9992]">No groups yet. Create them from profile menu → Rx Group.</div>}
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {rxGroups.map((g) => (
                    <button key={g.id} onClick={() => applyRxGroup(g)} className="w-full text-left rounded-lg px-2 py-1.5 hover:bg-[#F3F1EC] text-sm" data-testid={`rx-apply-${g.id}`}>
                      <span className="font-semibold text-[#0A4C36]">{g.name}</span> <span className="text-[10px] text-[#8A9992]">· {(g.drugs || []).length} drugs</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-4">
              <MedicineSearch onPickMedicine={addMedicine} onPickGroup={applyRxGroup} placeholder="Search medicine or Rx group…" />
            </div>

            <div className="space-y-3">
              {meds.length === 0 && (
                <div className="text-xs text-[#8A9992] py-8 text-center bg-white rounded-2xl border border-dashed border-[#E8E6E1]">
                  Search for a medicine above or apply an Rx group.
                </div>
              )}
              {meds.map((m, i) => (
                <MedicationRow
                  key={i} index={i} value={m}
                  onChange={(nv) => setMeds(meds.map((x, ix) => ix === i ? nv : x))}
                  onRemove={() => setMeds(meds.filter((_, ix) => ix !== i))}
                />
              ))}
              <button className="btn-outline-emerald w-full sm:w-auto" onClick={() => setMeds([...meds, emptyMed()])} data-testid="add-med-btn">
                <Plus size={14} /> Add custom drug
              </button>
            </div>
          </div>

          {/* Lab orders — full-width, roomy grid layout */}
          <div className="card-soft p-5 md:p-7" data-testid="labs-column">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <div className="field-label">Lab orders</div>
              {labs.length > 0 && (
                <span className="text-[11px] text-[#8A9992]">{labs.length} test{labs.length === 1 ? "" : "s"}</span>
              )}
            </div>
            <div className="space-y-3">
              {labs.length === 0 && (
                <div className="text-xs text-[#8A9992] py-8 text-center bg-[#F7F4EC]/60 rounded-2xl border border-dashed border-[#E8E6E1]">
                  No lab tests ordered.
                </div>
              )}
              {labs.map((l, i) => (
                <div key={i} className="bg-white rounded-2xl p-3 md:p-4 border border-[#EFECE5]" data-testid={`lab-row-${i}`}>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3">
                    <input
                      value={l.name}
                      onChange={(e) => setLabs(labs.map((x, ix) => ix === i ? { ...x, name: e.target.value } : x))}
                      className="input-clean md:col-span-6"
                      placeholder="Test name (CBC, LFT, Chest X-ray…)"
                    />
                    <select
                      value={l.urgency}
                      onChange={(e) => setLabs(labs.map((x, ix) => ix === i ? { ...x, urgency: e.target.value } : x))}
                      className="input-clean md:col-span-2"
                    >
                      <option value="routine">Routine</option>
                      <option value="urgent">Urgent</option>
                      <option value="stat">STAT</option>
                    </select>
                    <input
                      value={l.notes}
                      onChange={(e) => setLabs(labs.map((x, ix) => ix === i ? { ...x, notes: e.target.value } : x))}
                      className="input-clean md:col-span-3"
                      placeholder="Notes (e.g. fasting)"
                    />
                    <button
                      type="button"
                      onClick={() => setLabs(labs.filter((_, ix) => ix !== i))}
                      title="Remove lab order"
                      className="w-full md:w-auto md:col-span-1 h-10 md:h-auto inline-flex items-center justify-center rounded-xl border border-[#EFECE5] text-red-500 hover:bg-red-50"
                      data-testid={`lab-remove-${i}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
              <button className="btn-outline-emerald w-full sm:w-auto" onClick={() => setLabs([...labs, emptyLab()])} data-testid="add-lab-btn">
                <Plus size={14} /> Add lab order
              </button>
            </div>
          </div>
          </>
        )}
        </section>
      </div>

      {/* Nurse read-only banner */}
      {isNurseOnly && (
        <div className="mt-6 rounded-2xl bg-[#FFF3D6] border border-[#F0D6A0] p-4 text-sm text-[#8A5A34]" data-testid="nurse-readonly-banner">
          <b>Nurse view.</b> You can review the patient's history and capture vitals from the Workflow page. Diagnosis, Prescription and Save actions are reserved for the doctor.
        </div>
      )}

      {/* Desktop bottom Save & Prescribe — big and unmissable at the end of the form */}
      {!isNurseOnly && (
      <div className="hidden lg:flex justify-end mt-6" data-testid="bottom-save-row">
        <button
          onClick={save}
          disabled={saveBusy}
          className="btn-emerald text-base px-8 py-3"
          data-testid="save-consult-btn-bottom"
        >
          <Save size={16} /> {saveBusy ? "Saving…" : "Save & prescribe"}
        </button>
      </div>
      )}

      {/* Mobile-only sticky save bar — always reachable on long forms */}
      {!isNurseOnly && (
      <div className="lg:hidden sticky bottom-0 left-0 right-0 -mx-6 mt-6 px-4 py-3 bg-[#FDFBF7]/95 backdrop-blur border-t border-[#EFECE5] flex gap-2 z-30" data-testid="mobile-save-bar">
        <button onClick={() => setOpenVoice(true)} className="btn-outline-emerald flex-1 justify-center" data-testid="mobile-voice-btn">
          <Mic size={16} /> Voice
        </button>
        <button onClick={save} disabled={saveBusy} className="btn-emerald flex-[2] justify-center" data-testid="mobile-save-btn">
          <Save size={16} /> {saveBusy ? "Saving…" : "Save & prescribe"}
        </button>
      </div>
      )}

      <BillDialog open={openBill} onOpenChange={setOpenBill} patient={patient} consultationId={saved?.id} />
      <VideoCallDialog
        open={openCall}
        onOpenChange={setOpenCall}
        appointment={appointment || { id: appointmentId, patient_name: patient?.name, meeting_room: null }}
        doctorName={user?.name}
        onRoomChange={(room) => setAppointment((a) => a ? { ...a, meeting_room: room } : a)}
      />
      <VoiceConsultDialog
        open={openVoice}
        onOpenChange={setOpenVoice}
        patientId={patientId}
        onApply={applyVoice}
      />
    </Layout>
  );
}

function Row({ label, hint, children }) {
  return (
    <div>
      <label className="field-label block mb-1">{label}</label>
      {hint && <div className="text-[11px] text-[#8A9992] mb-2">{hint}</div>}
      {children}
    </div>
  );
}
function Vital({ label, v, setV, placeholder, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-[#8A9992] mb-1">{label}</div>
      <input value={v} onChange={(e) => setV(e.target.value)} className="input-clean px-3 py-2 text-sm" placeholder={placeholder} />
    </div>
  );
}
